import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { MockBanner } from "@/components/mock-banner";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { buildNexusLoginUrl, getLoginMethods } from "@/lib/api/auth";
import { consumeAuthFlash } from "@/lib/auth-flash";
import { getAccessToken } from "@/lib/auth-storage";
import { APP_NAME, MOCK_API } from "@/lib/constants";
import { MOCK_ADMIN, MOCK_PRODUCT, MOCK_STAKEHOLDER } from "@/lib/mock/data";
import { mockTokenForRole } from "@/lib/mock/store";
import type { UserRole } from "@/types";

export const Route = createFileRoute("/login")({
  beforeLoad: () => {
    if (getAccessToken()) {
      throw redirect({ to: "/" });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [authError] = useState(() => consumeAuthFlash());

  const methodsQuery = useQuery({
    queryKey: ["auth", "login-methods"],
    queryFn: getLoginMethods,
    enabled: !MOCK_API,
  });

  const enterMock = (role: UserRole) => {
    const user =
      role === "admin"
        ? MOCK_ADMIN
        : role === "product"
          ? MOCK_PRODUCT
          : MOCK_STAKEHOLDER;
    const token = mockTokenForRole(role);
    login(token, token, user);
    void navigate({ to: "/" });
  };

  const nexusBase = methodsQuery.data?.nexus_login_url?.trim() ?? "";
  const nexusUrl = nexusBase ? buildNexusLoginUrl(nexusBase) : "";
  const ssoEnabled = methodsQuery.data?.sso_enabled !== false;
  // After a failed SSO round-trip, stay on this page so the flash is visible
  // instead of bouncing straight back to Nexus.
  const shouldAutoRedirect =
    !MOCK_API && !authError && ssoEnabled && Boolean(nexusUrl);

  useEffect(() => {
    if (!shouldAutoRedirect) return;
    window.location.replace(nexusUrl);
  }, [shouldAutoRedirect, nexusUrl]);

  return (
    <div className="flex min-h-svh flex-col bg-background">
      <header className="bg-primary text-primary-foreground shadow-md">
        <div className="mx-auto flex min-h-[52px] w-full max-w-[1200px] items-center justify-between px-4 sm:px-6 lg:px-8">
          <span className="font-heading text-xl leading-none font-semibold tracking-tight">
            Open <span className="text-white/55">Backlog</span>
          </span>
          <ThemeToggle className="border-white/35 bg-white/10 text-white hover:bg-white/20 hover:text-white" />
        </div>
      </header>
      <MockBanner />

      <main className="flex flex-1 items-center justify-center px-4 py-16">
        <div className="w-full max-w-sm">
          <p className="mb-1.5 text-xs font-medium tracking-widest text-primary uppercase">
            {APP_NAME}
          </p>
          <h1 className="font-heading text-3xl leading-tight font-semibold tracking-tight">
            Sign in
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {MOCK_API
              ? "Mock mode — pick a role to explore the board without the API."
              : shouldAutoRedirect
                ? "Redirecting to Nexus…"
                : "Continue with your Nexus account to submit and track feature requests for Lion Parcel."}
          </p>

          {authError ? (
            <p
              role="alert"
              className="mt-4 rounded-lg bg-destructive/10 px-3.5 py-3 text-sm font-medium text-destructive ring-1 ring-destructive/25"
            >
              {authError}
            </p>
          ) : null}

          <div className="mt-7 grid gap-2.5 border-t border-border pt-6">
            {MOCK_API ? (
              <>
                <Button
                  type="button"
                  size="lg"
                  className="w-full"
                  onClick={() => enterMock("stakeholder")}
                >
                  Continue as Stakeholder
                </Button>
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  className="w-full"
                  onClick={() => enterMock("product")}
                >
                  Continue as Product
                </Button>
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  className="w-full"
                  onClick={() => enterMock("admin")}
                >
                  Continue as Admin
                </Button>
                <p className="pt-1 text-center text-xs leading-relaxed text-muted-foreground">
                  Seed data includes submitted, under review, approved, and
                  rejected requests.
                </p>
              </>
            ) : methodsQuery.isLoading || shouldAutoRedirect ? (
              <div className="flex flex-col items-center gap-3 py-4">
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
                {shouldAutoRedirect ? (
                  <p className="text-center text-xs text-muted-foreground">
                    Taking you to{" "}
                    <a
                      href={nexusUrl}
                      className="font-medium underline-offset-4 hover:underline"
                    >
                      Nexus SSO
                    </a>
                    …
                  </p>
                ) : null}
              </div>
            ) : methodsQuery.isError ? (
              <p className="rounded-lg bg-destructive/10 px-3.5 py-3 text-sm font-medium text-destructive ring-1 ring-destructive/25">
                Could not load login methods. Is the API running?
              </p>
            ) : ssoEnabled && nexusUrl ? (
              <Button
                type="button"
                size="lg"
                className="w-full"
                onClick={() => {
                  window.location.href = nexusUrl;
                }}
              >
                Sign in with Nexus
              </Button>
            ) : (
              <p className="rounded-lg bg-muted px-3.5 py-3 text-sm leading-relaxed text-muted-foreground ring-1 ring-border">
                Nexus SSO is not configured yet. Set{" "}
                <code className="font-mono text-xs">nexus_login_url</code> in
                server config — or run{" "}
                <code className="font-mono text-xs">pnpm dev:mock</code>.
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
